import { Link } from 'react-router-dom';
import type { EscoHrWorkforceSummaryResponse } from '../types';
import { displayStatusLabels, formatWhen, LOCKED_MESSAGE, LOCKED_TITLE, lockedReason, PLAN_REASONS, statusNote } from '../escoHrCopy';

export function SyntheticBadge() {
  return <span className="badge badge--caution">Synthetic Demo Data</span>;
}

const staleReasons: Record<string, string> = {
  SyncError: 'the latest event from escoHR could not be accepted',
  Suspended: 'the company is no longer entitled to this capability',
  Disconnected: 'the connection to escoHR has been disconnected',
};

/**
 * The one shared escoHR connection status shown at the top of Time & Attendance, Time Off, Holidays and Expenses: the status, the subscription, the
 * last successful and attempted sync, what needs attention, what is ready for payroll (always none in this version) and sync errors. It never
 * claims a connection or readiness the server did not report, and it uses the same approved copy as the Time & Attendance panel.
 */
export function EscoHrConnectionBanner({ summary, heading }: { summary: EscoHrWorkforceSummaryResponse; heading: string }) {
  const { status, domain } = summary;
  const conn = status.connection;
  const note = statusNote(status.displayStatus);
  const subscription = status.entitlement === 'Entitled' ? 'Included'
    : status.entitlement === 'Unknown' ? 'Unavailable'
      : lockedReason(status.entitlementReason);

  return (
    <section className="card card--wide" aria-label="escoHR connection status" data-testid="escohr-connection-banner">
      <div className="section-header">
        <h3>{heading}</h3>
        <span className={`badge ${status.displayStatus === 'Operational' ? '' : 'badge--caution'}`} data-testid="escohr-banner-status">
          {displayStatusLabels[status.displayStatus]}
        </span>
      </div>

      {status.entitlement === 'NotEntitled' && (
        <section className="access-restriction" role="region" aria-label={LOCKED_TITLE} data-testid="escohr-banner-locked">
          <h4 className="access-restriction__title"><span aria-hidden="true">🔒</span> {LOCKED_TITLE}</h4>
          <p>{LOCKED_MESSAGE}</p>
          <p className="muted">{lockedReason(status.entitlementReason)}</p>
          {status.entitlementReason && PLAN_REASONS.has(status.entitlementReason) && (
            <Link className="access-restriction__cta" to="/settings/subscription">View Plans</Link>
          )}
        </section>
      )}

      {status.entitlement === 'Unknown' && (
        <p className="muted" data-testid="escohr-banner-unavailable">
          The status of this capability is temporarily unavailable. Nothing is shown as configured or locked until it can be confirmed.
        </p>
      )}

      {status.entitlement === 'Entitled' && conn === null && (
        <p data-testid="escohr-banner-setup">
          The escoHR connection is not set up for this company. <Link to="/time-attendance">Set it up in Time &amp; Attendance</Link>.
        </p>
      )}

      {note && <p data-testid="escohr-banner-note">{note}</p>}

      {domain.stale && (
        <p role="status" data-testid="escohr-banner-stale">
          <strong>These records may be out of date.</strong> The last data received was {formatWhen(domain.dataAsOfUtc)}; {staleReasons[domain.staleReason ?? ''] ?? 'the connection is not healthy'}.
        </p>
      )}

      <dl className="access-restriction__facts">
        <div><dt>Connection status</dt><dd>{displayStatusLabels[status.displayStatus]}</dd></div>
        <div><dt>Subscription</dt><dd>{subscription}</dd></div>
        <div><dt>Last successful sync</dt><dd>{formatWhen(conn?.lastSuccessfulSyncAtUtc)}</dd></div>
        <div><dt>Last attempted sync</dt><dd>{formatWhen(conn?.lastAttemptedSyncAtUtc)}</dd></div>
        <div><dt>Data as of</dt><dd>{formatWhen(domain.dataAsOfUtc)}</dd></div>
        <div><dt>Records requiring attention</dt><dd>{domain.needsAttention}</dd></div>
        <div><dt>Records ready for payroll</dt><dd>{domain.readyForPayroll} (nothing here is used in payroll yet)</dd></div>
        <div><dt>Synchronization errors</dt><dd>{domain.syncErrors}</dd></div>
      </dl>
    </section>
  );
}
